import { Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { map, Observable } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { OrderQueries } from 'src/app/shared/types/get-query';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { ImageService } from 'src/app/services/image.service';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { MatDialog } from '@angular/material/dialog';
import { OrderMapper } from '../../../services/order-mapper';
import { ColorShots, Order, OrderDetail, OrderStatus } from '../../../models/new/order';
import * as _ from 'lodash';
import { Shot, ShotApprovalStatus } from '../../../models/new/shot';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddProductModalComponent } from './add-product-modal/add-product-modal.component';
import { ApproveOrderNoteComponent } from './approve-order-note/approve-order-note';
import { Color, Size, SizeGroup, Style } from '../../../models/new/style';
import { ImageGalleryComponent } from 'src/app/shared/components/image-gallery/image-gallery.component';
import { Warehouse } from 'src/app/shared/types/warehouse.model';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { Paginable } from 'src/app/shared/types/paginable';
import { PaginationFilters } from '../../../models/new/paginationFilters';
import { ResumeMapper } from '../../../services/resume-mapper';
import { ShotResume } from '../../../models/new/shotResume';
import { AccordionService } from 'src/app/services/accordion.service';
import { ManageColorsSizesModalComponent } from './add-product-modal/manage-colors-sizes-modal/manage-colors-sizes-modal.component';
import { SubmitOrderModalComponent } from './submit-order-modal/submit-order-modal.component';
import { AllIsApprovedModalComponent } from './all-is-approved-modal/all-is-approved-modal.component';
import { OrderReadyForPickupModalComponent } from './order-ready-for-pickup-modal/order-ready-for-pickup-modal.component';
import { OrderProgressModalComponent } from './order-progress-modal/order-progress-modal.component';
import { OrderApproveModalComponent } from './order-approve-modal/order-approve-modal.component';
import { OrderCostChangesModalComponent } from './order-cost-changes-modal/order-cost-changes-modal.component';
import { SetFinalCostModalComponent } from './set-final-cost-modal/set-final-cost-modal.component';
import { AddShotModalComponent } from '../../company-template-detail/add-shot-modal/add-shot-modal.component';

@Component({
  selector: 'bt-company-order-detail-shots',
  standalone: false,
  templateUrl: './company-order-detail-shots.component.html',
  styleUrls: ['./company-order-detail-shots.component.scss']
})
export class CompanyOrderDetailShotsComponent implements OnInit {
  loading: boolean = false;
  currentWarehouse: Warehouse | null = null;
  shotImageLoadingState: Record<string, boolean> = {};
  ShotApprovalStatus = ShotApprovalStatus;
  OrderStatus = OrderStatus;

  orderDetails: Paginable<Selectable<OrderDetail>> = new Paginable<Selectable<OrderDetail>>();
  //dati originali prima del salvataggio
  //originalOrderDetails: Paginable<Selectable<OrderDetail>> = new Paginable<Selectable<OrderDetail>>();
  order: Order | null = null;
  orderDetailsFilters = new PaginationFilters();
  shotResume: ShotResume = new ShotResume();

  constructor(
    private route: ActivatedRoute,
    private appService: AppService,
    private graphQLService: GraphQLService,
    private imageService: ImageService,
    private utilitiesService: UtilitiesService,
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
    private orderMapper: OrderMapper,
    private resumeMapper: ResumeMapper,
    private router: Router,
    private accordionService: AccordionService
  ) {}

  ngOnInit(): void {
    this.route.parent?.params.subscribe((params) => {
      const orderId = params['orderId'];

      if (orderId != 'new') {
        this.getOrderInfo(Number(orderId));
      }
    });
  }

  getOrderResume(_orderId: number) {
    this.loading = true;
    this.graphQLService
      .getData(OrderQueries.GET_ORDER_RESUME, { orderId: _orderId })
      .pipe(map((res: any) => this.resumeMapper.toShotResume(res.data.orderShotsResume)))
      .subscribe({
        next: (data: ShotResume) => {
          this.shotResume = data;

          this.getOrderDetails(_orderId);
        },
        error: (err) => {
          this.utilitiesService.showSnackbar('Error during fetching order resume', SnackbarType.Error);
          console.error(err);
          this.loading = false;
        }
      });
  }

  noProducts() {
    return this.orderDetails.items.length == 0;
  }

  someShotsAreUploaded() {
    return (
      this.shotResume?.uploadedShots > 0 || this.shotResume?.approvedShots > 0 || this.shotResume?.rejectedShots > 0
    );
  }

  allShotsAre(shotApprovalStatus: ShotApprovalStatus) {
    if (shotApprovalStatus == ShotApprovalStatus.Approved) {
      return this.shotResume?.uploadedShots == this.shotResume.approvedShots;
    } else if (shotApprovalStatus == ShotApprovalStatus.Rejected) {
      return this.shotResume?.uploadedShots == this.shotResume.rejectedShots;
    } else if (shotApprovalStatus == ShotApprovalStatus.Uploaded) {
      return this.shotResume?.uploadedShots == this.shotResume.uploadedShots;
    }
  }

  someShotsAre(shotApprovalStatus: ShotApprovalStatus) {
    if (shotApprovalStatus == ShotApprovalStatus.Approved) {
      return this.shotResume?.approvedShots > 0;
    } else if (shotApprovalStatus == ShotApprovalStatus.Rejected) {
      return this.shotResume?.rejectedShots > 0;
    } else if (shotApprovalStatus == ShotApprovalStatus.Uploaded) {
      return this.shotResume?.uploadedShots > 0;
    }
  }

  approveAllShots(shotApprovalStatus: ShotApprovalStatus) {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: shotApprovalStatus == ShotApprovalStatus.Approved ? 'Approve All Shots' : 'Reject All Shots',
          message:
            'Are you sure you want to ' +
            (shotApprovalStatus == ShotApprovalStatus.Approved ? 'approve' : 'reject') +
            ' all shots?'
        }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            this.loading = true;
            this.graphQLService
              .getData(OrderQueries.UPDATE_SHOT_STATUS_BY_ORDER_ID, {
                updateShotStatusByOrderIdInput: { orderId: this.order?.id, statusId: shotApprovalStatus }
              })

              .subscribe({
                next: (data: any) => {
                  this.loading = false;

                  if (this.order?.id) {
                    this.getOrderResume(this.order.id);
                    this.getOrderDetails(this.order.id);

                    if (shotApprovalStatus == ShotApprovalStatus.Approved) {
                      this.dialog
                        .open(AllIsApprovedModalComponent)
                        .afterClosed()
                        .subscribe((result) => {
                          if (result) {
                            this.sendToPhotographer(OrderStatus.Approved);
                          }
                        });
                    }
                  }
                },
                error: (err) => {
                  this.utilitiesService.showSnackbar(
                    err.networkError.error.errors[0].message || 'Error during approving all shots',
                    SnackbarType.Error
                  );
                  console.error(err);
                  this.loading = false;
                }
              });
          }
        }
      });
  }

  pageEvent(event: { pageNumber: number; pageSize: number }) {
    this.orderDetails.currentPage = event.pageNumber;
    this.orderDetails.size = event.pageSize;

    this.orderDetailsFilters.page = event.pageNumber;
    this.orderDetailsFilters.size = event.pageSize;

    this.refreshNavigation();
  }

  refreshNavigation() {
    this.loading = true;

    this.router.navigate([location.pathname], {
      queryParams: { page: this.orderDetailsFilters.page, size: this.orderDetailsFilters.size }
    });
  }

  getOrderInfo(_orderId: number): void {
    this.loading = true;
    this.graphQLService
      .getData(OrderQueries.GET_ORDERS, { where: { orderId: { eq: _orderId } }, first: 1 })
      .pipe(map((res: any) => this.orderMapper.toOrder(res.data.orders.nodes[0])))
      .subscribe({
        next: (data: Order) => {
          this.order = data;

          if (this.order.warehouse) {
            this.currentWarehouse = _.cloneDeep(this.order.warehouse);
          }

          if (this.order?.id) {
            this.getOrderResume(this.order.id);
          }

          /*this.checkOrderCostChanges(_orderId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (hasChanges) => {
					if (hasChanges) {
						// Cost changes modal takes priority
						this.dialog
							.open(OrderCostChangesModalComponent, {
								data: { orderId: this.order?.id }
							})
							.afterClosed()
							.subscribe((result) => {
								if (result) {
									this.ngOnInit();
								}
							});
					} else {
						
					}

					
				}
			});*/

          // Only show these modals if no cost changes
          if (this.order?.status?.id == OrderStatus.ReadyForPickup) {
            this.dialog.open(OrderReadyForPickupModalComponent);
          } else if (this.order?.status?.id == OrderStatus.InProgress) {
            this.dialog.open(OrderProgressModalComponent);
          } else if (this.order?.status?.id == OrderStatus.ShootCompleted) {
            this.dialog.open(OrderApproveModalComponent);
          }
        },
        error: (err) => {
          this.utilitiesService.showSnackbar('Error during fetching order info', SnackbarType.Error);
          console.error(err);
          this.loading = false;
        }
      });
  }

  checkOrderCostChanges(orderId: number): Observable<boolean> {
    return this.graphQLService.getData(OrderQueries.CHECK_ORDER_COST_CHANGES, {}).pipe(
      map((res: any) => res.data.checkOrderCostChanges),
      map((costChanges: any[]) => costChanges.some((item: any) => item.orderId === orderId))
    );
  }

  getOrderDetails(orderId: number): void {
    this.loading = true;

    this.route.queryParams.subscribe((params: Params) => {
      this.orderDetailsFilters = new PaginationFilters();
      this.orderDetailsFilters.page = params['page'] ? Number(params['page']) : 1;
      this.orderDetailsFilters.size = params['size'] ? Number(params['size']) : 20;

      this.orderDetails.currentPage = this.orderDetailsFilters.page || 1;
      this.orderDetails.size = this.orderDetailsFilters.size || 20;

      const pagination = this.orderDetails.buildQuery(this.orderDetails.currentPage);

      this.graphQLService
        .getData(OrderQueries.GET_ORDER_DETAILS, {
          orderId: orderId,
          ...pagination
        })
        .pipe(
          map((res: any) => {
            const orderDetails = res.data.orderDetails;
            console.log(orderDetails);
            //stampa in console lo stato di accordionService
            const paginable = new Paginable<Selectable<OrderDetail>>({
              items: orderDetails.nodes.map((csd: any) => {
                return {
                  ...this.orderMapper.toOrderDetail(csd),
                  selected: false,
                  opened: true
                };
              })
            });

            //nella warehousearticle, filtro solo i colori del magazzino selezionto, se esiste
            if (this.currentWarehouse) {
              paginable.items.forEach((item: Selectable<OrderDetail>) => {
                if (item.style?.warehouseArticles)
                  item.style.warehouseArticles = item.style?.warehouseArticles?.filter(
                    (wa) => wa.warehouse?.id == this.currentWarehouse?.id
                  );
              });
            }

            paginable.totalCount = orderDetails.totalCount;
            paginable.currentPage = this.orderDetailsFilters.page || 1;
            paginable.size = this.orderDetailsFilters.size || 2;

            return paginable;
          })
        )
        .subscribe({
          next: (data) => {
            this.orderDetails = data;
            this.initializeShotImageLoadingStates();
            //this.originalOrderDetails = _.cloneDeep(data);

            this.loading = false;
          },
          error: (err) => {
            this.utilitiesService.showSnackbar('Error during fetching order details', SnackbarType.Error);
            console.error(err);
            this.loading = false;
          }
        });
    });
  }

  getShotsByProduct(product: OrderDetail, status?: ShotApprovalStatus): Shot[] {
    const shots: Shot[] = [];
    this.orderDetails.items.forEach((d) => {
      if (d.style?.modelId !== product.style?.modelId) return;
      d.colorShots?.forEach((csg) => {
        csg.shots?.forEach((shot) => {
          if (status === undefined) {
            shots.push(shot);
          } else if (shot.shotApprovalStatus == status) {
            shots.push(shot);
          }
        });
      });
    });
    return shots;
  }

  getShotIdsByColor(color: ColorShots): number[] {
    const shotIds: number[] = [];
    this.orderDetails.items.forEach((d) => {
      d.colorShots?.forEach((csg) => {
        if (csg.color?.id === color.color?.id) {
          csg.shots?.forEach((shot) => {
            if (shot.id && shot.shotApprovalStatus != ShotApprovalStatus.NotUploaded) shotIds.push(shot.id);
          });
        }
      });
    });
    return shotIds;
  }

  viewGallery(shots: Shot[], first?: number /*id del primo shot da visualizzare */): void {
    this.dialog
      .open(ImageGalleryComponent, {
        data: { shots: shots, first: first }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          // handle result if needed
        }
      });
  }

  getShotsWithImages(colorShots: ColorShots): Shot[] {
    return colorShots.shots?.filter((s) => s.imgPath) || [];
  }

  removeProduct(product: Selectable<OrderDetail>) {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Remove Product',
          message: 'Are you sure you want to remove "' + product.style?.name + ' - ' + product.style?.code + '"?'
        }
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.loading = true;
          this.graphQLService
            .postData(MutationQuery.REMOVE_PRODUCTS_FROM_ORDER, {
              removeProductsFromOrderInput: { orderId: this.order?.id!, modelIds: [product.style?.modelId!] }
            })
            .subscribe({
              next: (res: any) => {
                this.loading = false;
                this.utilitiesService.showSnackbar('Product removed successfully', SnackbarType.Success);
                this.ngOnInit();
              },
              error: (err) => {
                this.loading = false;
                this.utilitiesService.showSnackbar(
                  err.networkError.error.errors[0].message || 'Error during removing product from order',
                  SnackbarType.Error
                );
              }
            });
        }
      });
  }

  removeColor(product: Selectable<OrderDetail>, color: Color) {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Remove Color',
          message:
            'Are you sure you want to remove "' +
            color.name +
            '" color from "' +
            product.style?.name +
            ' - ' +
            product.style?.code +
            '"?'
        }
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.loading = true;
          this.graphQLService
            .postData(MutationQuery.REMOVE_COLORS_FROM_PRODUCT, {
              removeColorsFromProductInput: {
                orderId: this.order?.id!,
                modelId: product.style?.modelId!,
                colorIds: [color.id!]
              }
            })
            .subscribe({
              next: (res: any) => {
                this.loading = false;
                this.utilitiesService.showSnackbar('Color removed successfully', SnackbarType.Success);
                this.ngOnInit();
              },
              error: (err) => {
                this.loading = false;
                this.utilitiesService.showSnackbar(
                  err.networkError.error.errors[0].message || 'Error during removing color from product',
                  SnackbarType.Error
                );
              }
            });
        }
      });
  }

  addProductModal(): void {
    this.dialog
      .open(AddProductModalComponent, {
        data: { warehouse: this.currentWarehouse ?? null, isFake: this.order?.isFake, orderId: this.order?.id }
      })
      .afterClosed()
      .subscribe((result: { warehouse: Warehouse | null; styles: Array<Style> }) => {
        if (!result) return;

        this.loading = true;

        if (this.currentWarehouse == null && result.warehouse != null) {
          this.currentWarehouse = _.cloneDeep(result.warehouse);
        }

        this.graphQLService
          .postData(MutationQuery.ADD_PRODUCTS_TO_ORDER, {
            addproductToOrderInput: {
              orderId: this.order?.id!,
              stylesColors: result.styles.map((s: Style) => ({
                modelId: s.modelId,
                colorSizes: s.colors
                  ? s.colors.map((c) => ({
                      colorId: c.id,
                      sizeId: c.size?.id
                    }))
                  : []
              })),
              warehouseId: result.warehouse?.id ?? null
            }
          })
          .subscribe({
            next: (res: any) => {
              this.ngOnInit();
              this.utilitiesService.showSnackbar('Products added successfully', SnackbarType.Success);
            },
            error: (err) => {
              this.loading = false;
              this.utilitiesService.showSnackbar(
                err.networkError.error.errors[0].message || 'Error during adding products to order',
                SnackbarType.Error
              );
            }
          });
      });
  }

  openManageColorsSizesModal(product: Selectable<OrderDetail>, color: Color): void {
    //siccome il colore si trova in colorShots, metto il colore dentro l'oggetto style

    if (!product.style) return;

    product.style.colors = [];
    product.style.colors?.push(
      new Color({
        id: color.id,
        code: color.code,
        name: color.name,
        size: color.size ? new Size({ id: color.size.id, name: color.size.name }) : undefined
      })
    );

    this.dialog
      .open(ManageColorsSizesModalComponent, {
        data: {
          style: product.style,
          singleColorSelection: true
        }
      })
      .afterClosed()
      .subscribe({
        next: (
          res: { color: Selectable<Color>; sizesAndQuantity: Selectable<{ size: Size; availableQty: number }>[] }[]
        ) => {
          if (res) {
            this.loading = true;
            this.graphQLService
              .postData(MutationQuery.UPDATE_ORDER_SIZE_QUANTITY, {
                updateOrderSizeQuantityInput: {
                  orderId: this.order?.id!,
                  modelId: product.style?.modelId!,
                  colorId: color.id!,
                  sizeId: res[0].sizesAndQuantity.filter((f) => f.selected)[0].size.id!,
                  quantity: 1
                }
              })
              .subscribe({
                next: (_res: any) => {
                  product.colorShots?.forEach((cs) => {
                    if (cs.color?.id == res[0].color.id) {
                      if (cs.color) cs.color.size = res[0].sizesAndQuantity.filter((f: any) => f.selected)[0].size;
                    }
                  });
                  this.utilitiesService.showSnackbar('Order size quantity updated successfully', SnackbarType.Success);
                  this.loading = false;
                },
                error: (err) => {
                  this.loading = false;
                  this.utilitiesService.showSnackbar(
                    err.networkError.error.errors[0].message || 'Error during updating order size quantity',
                    SnackbarType.Error
                  );
                }
              });
          }
        }
      });
  }

  downloadShots(shotIds: number[]): void {
    this.loading = true;
    this.imageService.downloadImagesByShotIds(shotIds).subscribe({
      next: (res: Blob) => {
        this.loading = false;
        const url = window.URL.createObjectURL(res);
        window.open(url, '_blank');
        this.utilitiesService.showSnackbar('Download started', SnackbarType.Success);
      },
      error: (err) => {
        this.loading = false;
        //this.utilitiesService.showSnackbar('Failed to start download', SnackbarType.Error);
      }
    });
  }

  downloadAllShots() {
    this.loading = true;
    if (this.order?.id) {
      this.imageService.downloadImagesByOrderIds([this.order.id]).subscribe({
        next: (res: Blob) => {
          this.loading = false;
          const url = window.URL.createObjectURL(res);
          window.open(url, '_blank');
        },
        error: (err) => {
          this.loading = false;
          //this.utilitiesService.showSnackbar('Failed to start download', SnackbarType.Error);
        }
      });
    }
  }

  thereAreImages(): boolean {
    return this.shotResume.uploadedShots > 0 || this.shotResume.approvedShots > 0 || this.shotResume.rejectedShots > 0;
  }

  copyToClipboard(text: string): void {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.utilitiesService.showSnackbar('Filename copied to clipboard', SnackbarType.Success);
      })
      .catch((err) => {
        this.utilitiesService.showSnackbar('Failed to copy filename', SnackbarType.Error);
        console.error(err);
      });
  }

  approveShots(shotIds: number[], status: number): void {
    //filtro sempre per gli shot id che hanno tassativamente lo stato uploaded
    shotIds = shotIds.filter((id) => {
      let shotStatus;
      this.orderDetails.items.forEach((d: OrderDetail) => {
        d.colorShots?.forEach((csg) => {
          csg.shots?.forEach((shot) => {
            if (shot.id == id) {
              shotStatus = shot.shotApprovalStatus;
            }
          });
        });
      });
      return shotStatus == ShotApprovalStatus.Uploaded ||
        shotStatus == ShotApprovalStatus.Rejected ||
        shotStatus == ShotApprovalStatus.Approved
        ? true
        : false;
    });

    this.loading = true;

    this.dialog
      .open(ApproveOrderNoteComponent, { data: status })
      .afterClosed()
      .subscribe({
        next: (result) => {
          this.loading = false;
          if (result != undefined) {
            const variables = {
              shotIds,
              statusId: status
              /*comments: result*/
            };
            this.loading = true;
            this.graphQLService
              .postData(MutationQuery.UPDATE_SHOT_STATUS, { updateShotStatusInput: variables })
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (res) => {
                  this.loading = false;
                  if ((res.data as any).updateShotStatus.success) {
                    this.orderDetails.items.forEach((d: OrderDetail) => {
                      d.colorShots?.forEach((csg) => {
                        csg.shots?.forEach((shot) => {
                          if (shot.id)
                            if (shotIds.includes(shot.id)) {
                              shot.shotApprovalStatus = status;
                            }
                        });
                      });
                    });

                    this.utilitiesService.showSnackbar(
                      'Shot approval state changed successfully',
                      SnackbarType.Success
                    );

                    if (this.order?.id) this.getOrderResume(this.order.id);
                  } else {
                    this.utilitiesService.showSnackbar('Error during shot approvation', SnackbarType.Error);
                  }
                },
                error: (err) => {
                  this.loading = false;
                  this.utilitiesService.showSnackbar(
                    err.networkError.error.errors[0].message || 'Error during shot approvation',
                    SnackbarType.Error
                  );
                  console.error(err);
                }
              });
          }
        }
      });
  }

  forbiddenEdit() {
    return (
      this.order?.status?.id == OrderStatus.Submitted ||
      this.order?.status?.id == OrderStatus.ReadyForPickup ||
      this.order?.status?.id == OrderStatus.ShootCompleted ||
      this.order?.status?.id == OrderStatus.Rejected ||
      this.order?.status?.id == OrderStatus.InProgress ||
      this.order?.status?.id == OrderStatus.Approved
    );
  }

  initializeShotImageLoadingStates() {
    const nextState: Record<string, boolean> = {};

    this.orderDetails.items.forEach((item) => {
      item.colorShots?.forEach((colorShot) => {
        colorShot.shots?.forEach((shot) => {
          const key = this.getShotImageKey(shot);

          if (!key || !shot.imgPath) return;

          nextState[key] = this.shotImageLoadingState[key] ?? true;
        });
      });
    });

    this.shotImageLoadingState = nextState;
  }

  getShotImageKey(shot: Shot): string {
    if (shot.id != undefined && shot.id != null) {
      return 'shot-' + shot.id;
    }

    return shot.imgPath || '';
  }

  isShotImageLoading(shot: Shot): boolean {
    if (!shot.imgPath) return false;

    const key = this.getShotImageKey(shot);
    return key ? this.shotImageLoadingState[key] !== false : false;
  }

  onShotImageLoad(shot: Shot): void {
    const key = this.getShotImageKey(shot);
    if (!key) return;

    this.shotImageLoadingState[key] = false;
  }

  onShotImageError(shot: Shot): void {
    const key = this.getShotImageKey(shot);
    if (!key) return;

    this.shotImageLoadingState[key] = false;
  }

  sendToPhotographer(status: OrderStatus) {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Confirm',
          message: 'Are you sure to confirm this order? ' + this.order?.photographer?.fullName + ' will be notified.'
        }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            //chiama l'UPDATE_ORDER_STATUS con order status Rejected se contiene shot rejected, altrimenti Approved
            //const hasRejectedShots = this.orderDetails.items.some((d: OrderDetail) => d.colorShots?.some((csg) => csg.shots?.some((shot) => shot.shotApprovalStatus == ShotApprovalStatus.Rejected)));

            let payload = {
              orderId: this.order?.id,

              orderStatusId: status
            };

            this.loading = true;
            this.graphQLService
              .postData(MutationQuery.UPDATE_ORDER_STATUS, { updateOrderStatusInput: payload })
              .subscribe({
                next: (value: any) => {
                  if (this.order && this.order.status) {
                    this.order.status.id = status;
                  }
                  this.loading = false;
                  this.ngOnInit();
                  this.utilitiesService.showSnackbar('Order completed successfully', SnackbarType.Success);
                },
                error: (err) => {
                  this.loading = false;
                  this.utilitiesService.showSnackbar(
                    err.networkError.error.errors[0].message || 'Error during sending order to photographer',
                    SnackbarType.Error
                  );
                  console.error(err);
                }
              });
          }
        }
      });
  }

  setAiGenerated(colorShots: ColorShots) {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: colorShots.isAi ? 'Remove AI generated' : 'Set as AI generated',
          message: colorShots.isAi
            ? 'Are you sure you want to remove this color as AI generated?'
            : 'Are you sure you want to set this color as AI generated?'
        }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            //colorShots.orderStyleId
            this.loading = true;
            this.graphQLService
              .postData(MutationQuery.SET_AI_GENERATED, {
                setIsAiInput: { orderStyleIds: [colorShots.orderStyleId], isAi: !colorShots.isAi }
              })
              .subscribe({
                next: (value: any) => {
                  this.loading = false;
                  this.ngOnInit();
                },
                error: (err) => {
                  this.loading = false;
                  this.utilitiesService.showSnackbar(
                    err.networkError.error.errors[0].message || 'Error during setting shots as AI generated',
                    SnackbarType.Error
                  );
                }
              });
          }
        }
      });
  }

  setAsReadyForPickup() {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Confirm',
          message: 'Are you sure you want to set this order as Ready for Pickup?'
        }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            let payload = {
              orderId: this.order?.id,
              orderStatusId: OrderStatus.ReadyForPickup
            };

            this.loading = true;
            this.graphQLService
              .postData(MutationQuery.UPDATE_ORDER_STATUS, { updateOrderStatusInput: payload })
              .subscribe({
                next: (value: any) => {
                  if (this.order && this.order.status) {
                    this.order.status.id = OrderStatus.ReadyForPickup;
                  }
                  this.loading = false;
                  this.ngOnInit();
                  this.utilitiesService.showSnackbar(
                    'Order set as Ready for Pickup successfully',
                    SnackbarType.Success
                  );
                },
                error: (err) => {
                  this.loading = false;
                  this.utilitiesService.showSnackbar(
                    err.networkError.error.errors[0].message || 'Error during setting order as Ready for Pickup',
                    SnackbarType.Error
                  );
                  console.error(err);
                }
              });
          }
        }
      });
  }

  openAddShotModal(color: ColorShots): void {
    this.dialog
      .open(AddShotModalComponent, {
        data: {
          orderId: this.order?.id,
          shots: color.shots || [],
          group: this.order?.orderType
        }
      })
      .afterClosed()
      .subscribe((result: Shot) => {
        if (result && color.orderStyleId) {
          this.loading = true;
          this.graphQLService
            .postData(MutationQuery.ADD_SHOT_TO_ORDER_STYLE, {
              addShotToOrderStyleInput: {
                orderStyleId: color.orderStyleId,
                shotTypeId: result.shotType?.id!,
                mediaFormatId: result.mediaFormat?.id!,
                aspectRatioId: result.aspectRatio?.id!
              }
            })
            .subscribe({
              next: () => {
                this.loading = false;
                this.utilitiesService.showSnackbar('Shot added successfully', SnackbarType.Success);
                if (this.order?.id) this.getOrderDetails(this.order.id);
              },
              error: (err) => {
                this.loading = false;
                this.utilitiesService.showSnackbar(
                  err.networkError.error.errors[0].message || 'Error during adding shot',
                  SnackbarType.Error
                );
                console.error(err);
              }
            });
        }
      });
  }

  removeShot(color: ColorShots, shot: Shot): void {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Remove shot',
          message: 'Are you sure you want to remove this shot?'
        }
      })
      .afterClosed()
      .subscribe((res) => {
        if (res && shot.id) {
          this.loading = true;
          this.graphQLService
            .postData(MutationQuery.REMOVE_SHOT_FROM_ORDER_STYLE, {
              removeShotFromOrderStyleInput: { orderStyleShotId: shot.id }
            })
            .subscribe({
              next: () => {
                this.loading = false;
                this.utilitiesService.showSnackbar('Shot removed successfully', SnackbarType.Success);
                if (this.order?.id) this.getOrderDetails(this.order.id);
              },
              error: (err) => {
                this.loading = false;
                this.utilitiesService.showSnackbar(
                  err.networkError.error.errors[0].message || 'Error during removing shot',
                  SnackbarType.Error
                );
                console.error(err);
              }
            });
        }
      });
  }

  openSetFinalCostModal(): void {
    if (!this.order?.id) {
      this.utilitiesService.showSnackbar('Order ID is missing', SnackbarType.Error);
      return;
    }

    this.dialog
      .open(SetFinalCostModalComponent, {
        data: {
          orderId: this.order.id,
          finalCost: this.shotResume.finalCost,
          totalSku: this.shotResume.totalSku
        }
      })
      .afterClosed()
      .subscribe((finalCost: number | undefined) => {
        if (typeof finalCost === 'number') {
          this.shotResume.finalCost = finalCost;
        }
      });
  }

  submitOrder() {
    if (!this.shotResume.finalCost) {
      this.utilitiesService.showSnackbar('Total cost is missing', SnackbarType.Error);

      this.openSetFinalCostModal();

      return;
    }

    this.dialog
      .open(SubmitOrderModalComponent, {
        data: { order: this.order }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            let payload = {
              orderId: this.order?.id,
              orderStatusId: OrderStatus.Submitted
            };

            this.loading = true;
            this.graphQLService
              .postData(MutationQuery.UPDATE_ORDER_STATUS, { updateOrderStatusInput: payload })
              .subscribe({
                next: (value: any) => {
                  if (this.order && this.order.status) {
                    this.order.status.id = OrderStatus.Submitted;
                  }
                  this.loading = false;
                  this.ngOnInit();

                  this.utilitiesService.showSnackbar('Order submitted successfully', SnackbarType.Success);
                },
                error: (err) => {
                  this.loading = false;
                  this.utilitiesService.showSnackbar(
                    err.networkError.error.errors[0].message || 'Error during submission',
                    SnackbarType.Error
                  );
                  console.error(err);
                }
              });
          }
        }
      });
  }

  toggle(product: Selectable<OrderDetail>) {
    product.opened = !product.opened;

    this.accordionService.companyOrderDetailsState.set(product.style?.modelId!, product.opened);
  }

  isVideo(shot: Shot): boolean {
    if (shot.imgPath) {
      let path = shot.imgPath.toLowerCase();
      if (path.lastIndexOf('.mp4') > 0 || path.lastIndexOf('.mkv') > 0) return true;
    }
    return false;
  }
}
